import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

/* ===================== helpers fechas ===================== */
function ymFirstDay(date) {
  const d = new Date(date.getFullYear(), date.getMonth(), 1);
  d.setHours(0, 0, 0, 0);
  return d;
}
function ymLastDay(date) {
  const d = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  d.setHours(23, 59, 59, 999);
  return d;
}
function addMonths(date, delta) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + delta);
  return d;
}
function fmtYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
// Rango N3/N6 excluyendo mes actual y el anterior (ej: hoy=Sep → N3 = May..Jul)
function getNXRangeExcludingCurrentPrev(n) {
  const today = new Date();
  const startMonth = addMonths(today, -(n + 1)); // -4 si n=3
  const endMonth   = addMonths(today, -2);       // siempre -2
  const from = ymFirstDay(startMonth);
  const to   = ymLastDay(endMonth);
  return { from, to };
}

export default function EstadisticasFijo() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [monthly, setMonthly] = useState([]); // [{ periodo: 'YYYY-MM', ventas: N }, ...]
  const [data, setData] = useState({
    periodos: { ventas: [], n3: [], n6: [] },
    top10Ventas: [],
    topPenalizadas3N: [],
    topPenalizadas6N: [],
  });

  /* ============ estado de filtros Top10 ============ */
  const [topLoading, setTopLoading] = useState(false);
  const [periodMode, setPeriodMode] = useState("n6"); // "n3" | "n6" | "custom"
  const [fromMonth, setFromMonth]   = useState("");   // YYYY-MM (para custom)
  const [toMonth, setToMonth]       = useState("");   // YYYY-MM (para custom)
  const [fSupervisor, setFSupervisor] = useState("");
  const [fCanal, setFCanal] = useState("");

  const resetTopFilters = () => {
    setPeriodMode("n6");   // valor por defecto
    setFromMonth("");
    setToMonth("");
    setFSupervisor("");
    setFCanal("");
    // aplicar luego de actualizar el estado
    setTimeout(() => applyTopFilters(), 0);
  };

  // ===== carga inicial (usa N6 por defecto) =====
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      window.location.href = "/login";
      return;
    }
    // 1) Carga N3/N6/Top10 (por defecto N6)
    const loadTopDefault = async () => {
      try {
        const { from, to } = getNXRangeExcludingCurrentPrev(6);
        const res = await axios.get("https://api.t-sales.cl/api/estadisticas/fijo/top", {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            ventas_from: fmtYMD(from),
            ventas_to: fmtYMD(to),
            limit: 10,
          },
        });
        setData(res.data);
      } catch {
        setErr("No se pudieron cargar las estadísticas.");
      } finally {
        setLoading(false);
      }
    };
    loadTopDefault();
  }, []);

  // 2) Carga ventas históricas por mes (no bloquea la pantalla)
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    axios
      .get("https://api.t-sales.cl/api/estadisticas/fijo/ventas-mensuales", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setMonthly(res.data?.rows || []))
      .catch(() => {});
  }, []);

  // ===== aplicar filtros Top10 =====
  const applyTopFilters = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      window.location.href = "/login";
      return;
    }
    setTopLoading(true);
    setErr("");

    // calcular rango según modo
    let from, to;
    if (periodMode === "n3") {
      const r = getNXRangeExcludingCurrentPrev(3);
      from = r.from; to = r.to;
    } else if (periodMode === "n6") {
      const r = getNXRangeExcludingCurrentPrev(6);
      from = r.from; to = r.to;
    } else {
      // custom: usar YYYY-MM seleccionados
      if (!fromMonth || !toMonth) {
        setErr("Debes elegir ambos meses para el rango personalizado.");
        setTopLoading(false);
        return;
      }
      const fm = fromMonth.split("-").map(Number);
      const tm = toMonth.split("-").map(Number);
      from = ymFirstDay(new Date(fm[0], fm[1] - 1, 1));
      to   = ymLastDay(new Date(tm[0], tm[1] - 1, 1));
    }

    try {
      const params = {
        ventas_from: fmtYMD(from),
        ventas_to: fmtYMD(to),
        limit: 50, // pedimos más y el backend devuelve ordenado; luego mostramos top 10
      };
      if (fSupervisor) params.supervisor = fSupervisor;
      if (fCanal) params.canal = fCanal;

      const res = await axios.get("https://api.t-sales.cl/api/estadisticas/fijo/top", {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      // si el backend nos trae más de 10, cortamos acá
      const next = { ...res.data };
      if (Array.isArray(next.top10Ventas) && next.top10Ventas.length > 10) {
        next.top10Ventas = next.top10Ventas.slice(0, 10);
      }
      setData(next);
    } catch {
      setErr("No se pudieron aplicar los filtros.");
    } finally {
      setTopLoading(false);
    }
  };

  const exportCalidad = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        "https://api.t-sales.cl/api/estadisticas/fijo/export-n-calidad",
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "calidad_fijo.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert("No se pudo exportar el CSV");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#006f91] text-white flex items-center justify-center">
        Cargando...
      </div>
    );
  }

  if (err && !topLoading) {
    // Nota: si err viene de filtros, igual mostramos la página y el error arriba de los filtros
  }

  const { periodos, top10Ventas, topPenalizadas3N, topPenalizadas6N } = data;

  return (
    <div className="min-h-screen bg-[#006f91] text-white px-4 py-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <header className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Estadísticas Fijo</h1>
          <button
            onClick={exportCalidad}
            className="bg-[#64FFDA] text-[#0F1C17] px-4 py-2 rounded-xl font-semibold hover:opacity-90"
          >
            📤 Export N3/N6 (mes)
          </button>
        </header>

        {/* Periodos visibles para contexto */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SmallCard title="Periodo Ventas (Top10)">
            <span className="text-sm">
              {periodos.ventas?.[0]} → {periodos.ventas?.[1]}
            </span>
          </SmallCard>
          <SmallCard title="Ventana N3 (Penalizadas)">
            <span className="text-sm">
              {periodos.n3?.[0]} → {periodos.n3?.[1]}
            </span>
          </SmallCard>
          <SmallCard title="Ventana N6 (Penalizadas)">
            <span className="text-sm">
              {periodos.n6?.[0]} → {periodos.n6?.[1]}
            </span>
          </SmallCard>
        </div>

        {/* ⬇️ HISTÓRICO POR MES (arriba del Top 10) */}
        <Block title="Ventas por mes (histórico)">
          {monthly.length === 0 ? (
            <div className="text-white/70 text-sm">Sin datos</div>
          ) : (
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <BarChart data={monthly} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.2)" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="periodo"
                    tick={{ fill: "#FFFFFF", fontSize: 12 }}
                    tickLine={{ stroke: "rgba(255,255,255,0.4)" }}
                    axisLine={{ stroke: "rgba(255,255,255,0.4)" }}
                    tickMargin={6}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fill: "#FFFFFF", fontSize: 12 }}
                    tickLine={{ stroke: "rgba(255,255,255,0.4)" }}
                    axisLine={{ stroke: "rgba(255,255,255,0.4)" }}
                    tickMargin={6}
                  />
                  <Tooltip
                    wrapperStyle={{ outline: "none" }}
                    contentStyle={{ backgroundColor: "#FFFFFF", borderColor: "#64FFDA", color: "#0F1C17" }}
                    labelStyle={{ color: "#0F1C17", fontWeight: 600 }}
                    itemStyle={{ color: "#0F1C17" }}
                    formatter={(value, name) => [value, name === "ventas" ? "Ventas" : name]}
                  />
                  <Bar dataKey="ventas" name="Ventas" fill="#64FFDA" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Block>

        {/* ===================== FILTROS TOP 10 ===================== */}
        <Block title="Top 10 Ejecutivos por Ventas (Fijo)">
          {/* Filtros en una sola línea (botones pasan abajo) */}
          <div className="mb-2 grid grid-cols-1 lg:grid-cols-12 items-center gap-2 lg:gap-3">
            {/* N3 / N6 / Rango - compacto */}
            <div className="lg:col-span-2">
              <div className="inline-flex w-fit bg-white/10 border border-white/20 rounded-lg overflow-hidden">
                <button
                  onClick={() => setPeriodMode("n3")}
                  className={`h-9 px-3 text-sm transition ${periodMode === "n3" ? "bg-[#64FFDA] text-[#0F1C17]" : "hover:bg-white/5 text-white"}`}
                >
                  N3
                </button>
                <button
                  onClick={() => setPeriodMode("n6")}
                  className={`h-9 px-3 text-sm transition border-l border-white/20 ${periodMode === "n6" ? "bg-[#64FFDA] text-[#0F1C17]" : "hover:bg-white/5 text-white"}`}
                >
                  N6
                </button>
                <button
                  onClick={() => setPeriodMode("custom")}
                  className={`h-9 px-3 text-sm transition border-l border-white/20 ${periodMode === "custom" ? "bg-[#64FFDA] text-[#0F1C17]" : "hover:bg-white/5 text-white"}`}
                >
                  Rango
                </button>
              </div>
            </div>

            {/* Desde / Hasta */}
            <div className="lg:col-span-2 min-w-0">
              <input
                type="month"
                aria-label="Desde"
                title="Desde"
                className="h-9 bg-white/10 border border-white/20 rounded-lg px-3 text-sm w-full placeholder-white/60"
                value={fromMonth}
                onChange={(e) => setFromMonth(e.target.value)}
                disabled={periodMode !== "custom"}
              />
            </div>
            <div className="lg:col-span-2 min-w-0">
              <input
                type="month"
                aria-label="Hasta"
                title="Hasta"
                className="h-9 bg-white/10 border border-white/20 rounded-lg px-3 text-sm w-full placeholder-white/60"
                value={toMonth}
                onChange={(e) => setToMonth(e.target.value)}
                disabled={periodMode !== "custom"}
              />
            </div>

            {/* Supervisor */}
            <div className="lg:col-span-3 min-w-0">
              <input
                type="text"
                placeholder="Supervisor…"
                className="h-9 bg-white/10 border border-white/20 rounded-lg px-3 text-sm w-full placeholder-white/60"
                value={fSupervisor}
                onChange={(e) => setFSupervisor(e.target.value)}
              />
            </div>

            {/* Canal */}
            <div className="lg:col-span-3 min-w-0">
              <input
                type="text"
                placeholder="Canal…"
                className="h-9 bg-white/10 border border-white/20 rounded-lg px-3 text-sm w-full placeholder-white/60"
                value={fCanal}
                onChange={(e) => setFCanal(e.target.value)}
              />
            </div>
          </div>

          {/* Botones abajo a la derecha */}
          <div className="mb-4 flex flex-col sm:flex-row gap-2 justify-end">
            <button
              onClick={resetTopFilters}
              className="h-9 px-4 rounded-xl text-sm border border-white/30 bg-white/10 hover:bg-white/20"
              disabled={topLoading}
            >
              Reiniciar filtros
            </button>
            <button
              onClick={applyTopFilters}
              disabled={topLoading}
              className="h-9 min-w-[140px] bg-[#64FFDA] text-[#0F1C17] px-4 rounded-xl font-semibold hover:opacity-90 disabled:opacity-60"
            >
              {topLoading ? "Aplicando..." : "Aplicar"}
            </button>
          </div>

          {err && (
            <div className="mb-3 text-sm bg-red-500/20 border border-red-400/40 text-red-100 rounded-lg px-3 py-2">
              {err}
            </div>
          )}

          <Table
            headers={["#", "Ejecutivo", "RUT", "Canal", "Supervisor", "Ventas"]}
            rows={(top10Ventas || []).map((r, idx) => [
              idx + 1,
              r.ejecutivo,
              r.rut || "—",
              r.canal || "—",
              r.supervisor || "—",
              r.ventas,
            ])}
            empty={topLoading ? "Cargando..." : "Sin datos"}
          />
        </Block>

        {/* TOP PENALIZADAS 3N */}
        <Block title="TOP PENALIZADAS 3N (últimos 3 meses)">
          <Table
            headers={["#", "Ejecutivo", "RUT", "Canal", "Supervisor", "Penalizadas"]}
            rows={(topPenalizadas3N || []).map((r, idx) => [
              idx + 1,
              r.ejecutivo,
              r.rut || "—",
              r.canal || "—",
              r.supervisor || "—",
              r.penalizadas,
            ])}
            empty="Sin datos"
          />
        </Block>

        {/* TOP PENALIZADAS 6N */}
        <Block title="TOP PENALIZADAS 6N (últimos 6 meses)">
          <Table
            headers={["#", "Ejecutivo", "RUT", "Canal", "Supervisor", "Penalizadas"]}
            rows={(topPenalizadas6N || []).map((r, idx) => [
              idx + 1,
              r.ejecutivo,
              r.rut || "—",
              r.canal || "—",
              r.supervisor || "—",
              r.penalizadas,
            ])}
            empty="Sin datos"
          />
        </Block>
      </div>
    </div>
  );
}

/* ===== UI helpers ===== */
function SmallCard({ title, children }) {
  return (
    <motion.div
      className="bg-white/10 border border-white/10 rounded-xl p-4"
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
    >
      <div className="text-xs text-white/80 mb-1">{title}</div>
      <div className="font-semibold">{children}</div>
    </motion.div>
  );
}
function Block({ title, children }) {
  return (
    <motion.div
      className="bg-white/10 border border-white/10 rounded-xl p-5"
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
    >
      <h3 className="text-lg font-bold mb-3">{title}</h3>
      {children}
    </motion.div>
  );
}
function Table({ headers, rows, empty }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left bg-white/10">
            {headers.map((h) => (
              <th key={h} className="px-3 py-2 whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(!rows || rows.length === 0) ? (
            <tr><td className="px-3 py-6 text-center text-white/70" colSpan={headers.length}>{empty}</td></tr>
          ) : (
            rows.map((r, i) => (
              <tr key={i} className="border-t border-white/10 hover:bg-white/5">
                {r.map((c, j) => (
                  <td key={j} className="px-3 py-2 whitespace-nowrap">{c}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
